import type { Ref } from 'react';
import { useRef, useState } from 'react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { useActiveBundle } from '../../../../context/active-bundle';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import { isEmptyString } from '@/utils/string';
import type { AppBundle } from '@/utils/app-bundles';
import type { NavItem as NavItemType, NavItemGroup } from '../main-navigation/routes';
import { isNavItemActive } from '../main-navigation/routes';
import { useFilterWorkspacesByName, useWorkspaceCount } from '../../../../features/workspaces/hooks';
import type { MergedWorkspace } from '../../../../features/workspaces';

import { Paragraph, Search, Box, Stack, InlineStack, NavItem, SideNavGroup } from '@customerio/pluma-components/react';
import { PlumaSideNav as PlumaSideNavCss } from '@customerio/pluma-components/css/components';
import { WorkspaceDot } from '../workspace-dot';
import { shouldProcessLinkClick } from '@/utils/navigation';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useAuth } from '@/services/auth';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useRouter } from '@tanstack/react-router';

function SettingsLinkItem(props: {
  item: NavItemType;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItemType) => unknown;
  activeApp: AppBundle | null;
}) {
  const activeRoute = useStore(activeRouteStore);

  return (
    <NavItem
      data-test-nav-item={props.item.id}
      href={props.item.href}
      icon={props.item.iconName}
      isSelected={isNavItemActive(props.item, activeRoute)}
      onClick={(event) =>
        props.onClick(
          // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
          event as React.MouseEvent<HTMLAnchorElement>,
          props.item,
        )
      }
    >
      {props.item.label}
    </NavItem>
  );
}

type WorkspaceLinkItemProps = {
  workspace: MergedWorkspace;
  onClick: (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavItemType,
    options?: TargetTransitionOptions,
  ) => unknown;
  currentWorkspaceRef: Ref<HTMLAnchorElement> | null;
};

function WorkspaceLinkItem({ workspace, onClick, currentWorkspaceRef }: WorkspaceLinkItemProps) {
  const activeRoute = useStore(activeRouteStore);
  const router = useRouter();
  const navItem: NavItemType = {
    id: `workspace_settings_${workspace.id}`,
    label: workspace.name,
    href: router.buildLocation({
      to: '/workspaces/$workspaceId/settings',
      params: { workspaceId: workspace.id },
    }).href,
  };

  return (
    <NavItem
      href={navItem.href}
      isSelected={isNavItemActive(navItem, activeRoute)}
      onClick={(event) =>
        onClick(
          // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
          event as React.MouseEvent<HTMLAnchorElement>,
          navItem,
        )
      }
      ref={currentWorkspaceRef}
    >
      <InlineStack alignBlock="center" gap="050">
        {!isEmptyString(workspace.color) ? <WorkspaceDot color={workspace.color} /> : null}
        <Box truncate={true}>{workspace.name}</Box>
      </InlineStack>
    </NavItem>
  );
}

type SettingsSideNavWorkspacesSectionProps = {
  onClick: (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavItemType,
    options?: TargetTransitionOptions,
  ) => unknown;
};

function SettingsSideNavWorkspacesSection({ onClick }: SettingsSideNavWorkspacesSectionProps) {
  const currentWorkspace = useCurrentWorkspace();

  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState<string>('');
  const workspaceCountQuery = useWorkspaceCount();
  const filteredWorkspacesQuery = useFilterWorkspacesByName(workspaceSearchQuery);
  const allWorkspacesCount = workspaceCountQuery.data ?? 0;
  const workspaceData = filteredWorkspacesQuery.data ?? [];

  const currentWorkspaceElementRef = useRef<HTMLAnchorElement | null>(null);

  useMountEffect(() => {
    if (currentWorkspaceElementRef.current == null) {
      return;
    }

    currentWorkspaceElementRef.current.scrollIntoView({
      block: 'center',
    });
  });

  return (
    <Stack mt="300" flexGrow="1" truncate={true} overflowX="hidden">
      {allWorkspacesCount > 3 ? (
        <Search
          value={workspaceSearchQuery}
          onSearchInput={setWorkspaceSearchQuery}
          onSearch={setWorkspaceSearchQuery}
          shouldSearchOnClear={true}
          shouldSearchOnInput={true}
          placeholder="Search&hellip;"
          autoFocus={true}
          label="Workspace settings"
          mb="200"
          icon={null}
        />
      ) : null}
      <Box
        overflowY="auto"
        flexGrow="1"
        h="0"
        style={{
          marginInline: `calc(-1 * ${PlumaSideNavCss.vars.paddingInline})`,
          paddingInline: PlumaSideNavCss.vars.paddingInline,
          scrollbarWidth: 'thin',
        }}
      >
        {workspaceData.length > 0 ? (
          workspaceData.map((w: MergedWorkspace) => (
            <WorkspaceLinkItem
              key={w.id}
              workspace={w}
              onClick={onClick}
              currentWorkspaceRef={w.id === currentWorkspace?.id ? currentWorkspaceElementRef : null}
            />
          ))
        ) : (
          <Paragraph variant="secondary">No results found</Paragraph>
        )}
      </Box>
    </Stack>
  );
}

export function SettingsNavigation() {
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const { hasPermission } = usePermissions();
  const { account } = useAuth();
  // Partner account provisioning is gated on the partner_account attribute
  // (AI-1594) and requires account/edit — the same permission the route enforces
  // — so we don't surface a link that would bounce the user back to /settings.
  const partnerEnabled = account?.partner_account === true && hasPermission('account/edit');

  const onClickNavItem = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavItemType,
    options?: TargetTransitionOptions,
  ) => {
    if (!shouldProcessLinkClick(event)) return;

    event.preventDefault();

    if (item.href == null) {
      return;
    }

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', options);
  };

  const items: NavItemGroup[] = [
    {
      items: [
        {
          id: 'account_settings',
          label: 'Account Settings',
          href: '/settings',
          iconName: 'company',
          activeExceptUrls: ['/settings/personal', '/settings/partner'],
        },
        {
          id: 'personal_settings',
          label: 'Personal Settings',
          href: '/settings/personal',
          iconName: 'workspaces',
        },
        ...(partnerEnabled === true
          ? [
              {
                id: 'partner_settings',
                label: 'Partner Settings',
                href: '/settings/partner',
                iconName: 'handshake' as const,
              },
            ]
          : []),
      ],
    },
  ];

  const navItemGroups = items.map((group, index) => {
    const navItems = group.items.map((item) => {
      return <SettingsLinkItem key={item.id} item={item} onClick={onClickNavItem} activeApp={activeApp} />;
    });

    return (
      <SideNavGroup key={group.title ?? `group_${index}`} title={group.title}>
        {navItems}
      </SideNavGroup>
    );
  });

  return (
    <>
      {navItemGroups}
      <SettingsSideNavWorkspacesSection onClick={onClickNavItem} />
    </>
  );
}
